import FilterSelect from '@Components/Filter/Dropdown';
import Image from '@Components/Image';
import { RootStateType, AppDispatch } from '@Store';
import { Box, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLoggedInUserSystemList } from '../../store/reducers/Systems';
import API from '@Services/apiAxios';
import { AnalyticsData } from './types/type';
import { formatNumber, useLocale } from '../../utils/formatNumber';
import { convertWeightKGtoLB } from '../../utils/convertWeight';

const TopPerformanceView = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [system, setSystem] = useState<string[]>([]);
  const { loggedInUserSystemDropdownList } = useSelector((state: RootStateType) => state.System);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const locale = useLocale();
  const defaultOrgCountryCurrency = useSelector(
    (state: RootStateType) => state.orgSetup.organizationData?.currency?.name || '',
  );
  // const [loading, setLoading] = useState<boolean>(true);
  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const { name = '' } = organizationData || {};

  const onSystemFilter = (selected: Array<string>) => {
    setSystem(selected);
  };

  useEffect(() => {
    dispatch(getLoggedInUserSystemList());
  }, []);

  const fetchAnalyticsStats = async () => {
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const response = await API.get('/analytics/stats/today', {
        params: {
          date: currentDate,
          'system[]': system,
        },
      });
      setAnalyticsData(response.data.data);
    } catch (error) {
      console.error('Error fetching analytics stats:', error);
    }
  };

  // console.info('analyticsData:', analyticsData);

  useEffect(() => {
    fetchAnalyticsStats();
  }, [system]);

  return (
    <Box
      sx={{
        borderRadius: '9px',
        border: '1px solid #F1F1F1',
        boxShadow: '0px 1px 4px 0px rgba(0, 0, 0, 0.25)',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
        padding: '20px 40px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row-reverse' }}>
        <Box
          sx={{
            width: { xs: '100%', sm: 'auto' },
            marginBottom: { xs: '20px', sm: '0' },
          }}
        >
          <FilterSelect
            id="system"
            label="Select system"
            options={loggedInUserSystemDropdownList}
            value={system}
            onChange={onSystemFilter}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 3, md: 0 },
          mb: 4,
        }}
      >
        <Box
          sx={{
            flexBasis: { xs: '100%', md: '20%' },
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'center' },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              fontSize: { xs: '20px', md: '24px' },
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: 0,
                width: '60px',
                height: '4px',
                backgroundColor: 'primary.main',
                borderRadius: '2px',
                display: { xs: 'block', md: 'none' },
              },
            }}
          >
            Today's Performance
          </Typography>
        </Box>

        <Box
          sx={{
            flexBasis: { xs: '100%', md: '80%' },
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
            gap: { xs: 3, sm: 2, md: 4 },
            alignItems: 'stretch',
          }}
        >
          {/* Shipment Card */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              padding: '20px',
              backgroundColor: 'background.paper',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '10px',
                backgroundColor: 'secondary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Image
                src="/images/icons/AdminHome/shipment.png"
                alt="shipment"
                style={{ width: '40px', height: '38px' }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  padding: '4px 8px',
                  borderRadius: '6px',
                  backgroundColor: analyticsData?.revenuePercentage >= 0 ? 'success.light' : 'error.light',
                  mb: 1.5,
                }}
              >
                <Image
                  src={
                    analyticsData?.revenuePercentage >= 0
                      ? '/images/icons/AdminHome/upArrow.svg'
                      : '/images/icons/AdminHome/downArrow.svg'
                  }
                  alt="trend"
                  style={{ width: '10px', height: '12px' }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: analyticsData?.revenuePercentage >= 0 ? 'success.main' : 'error.main',
                    fontSize: '12px',
                  }}
                >
                  {formatNumber(analyticsData?.revenuePercentage, locale)}%
                </Typography>
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '28px', sm: '32px' },
                  lineHeight: 1.2,
                  mb: 0.5,
                  fontFeatureSettings: '"tnum" on, "lnum" on',
                }}
              >
                {analyticsData?.totalShipment && formatNumber(analyticsData?.totalShipment, locale)}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Shipment
              </Typography>
            </Box>
          </Box>

          {/* Weight Card */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              padding: '20px',
              backgroundColor: 'background.paper',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '10px',
                backgroundColor: 'secondary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Image src="/images/icons/AdminHome/weight.png" alt="weight" style={{ width: '40px', height: '38px' }} />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  padding: '4px 8px',
                  borderRadius: '6px',
                  backgroundColor: analyticsData?.weightPercentage >= 0 ? 'success.light' : 'error.light',
                  mb: 1.5,
                }}
              >
                <Image
                  src={
                    analyticsData?.weightPercentage >= 0
                      ? '/images/icons/AdminHome/upArrow.svg'
                      : '/images/icons/AdminHome/downArrow.svg'
                  }
                  alt="trend"
                  style={{ width: '10px', height: '12px' }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: analyticsData?.weightPercentage >= 0 ? 'success.main' : 'error.main',
                    fontSize: '12px',
                  }}
                >
                  {formatNumber(analyticsData?.weightPercentage, locale)}%
                </Typography>
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '28px', sm: '32px' },
                  lineHeight: 1.2,
                  mb: 0.5,
                  fontFeatureSettings: '"tnum" on, "lnum" on',
                }}
              >
                {name === 'Cargo Solutions Network'
                  ? analyticsData?.totalWeight && formatNumber(convertWeightKGtoLB(analyticsData?.totalWeight), locale)
                  : analyticsData?.totalWeight && formatNumber(analyticsData?.totalWeight, locale)}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Total Weight {name === 'Cargo Solutions Network' ? '(LBS)' : '(KG)'}
              </Typography>
            </Box>
          </Box>

          {/* Revenue Card */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              padding: '20px',
              backgroundColor: 'background.paper',
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <Box
              sx={{
                width: '64px',
                height: '64px',
                borderRadius: '10px',
                backgroundColor: 'secondary.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Image
                src="/images/icons/AdminHome/revenue.png"
                alt="revenue"
                style={{ width: '40px', height: '38px' }}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  padding: '4px 8px',
                  borderRadius: '6px',
                  backgroundColor: analyticsData?.shipmentPercentage >= 0 ? 'success.light' : 'error.light',
                  mb: 1.5,
                }}
              >
                <Image
                  src={
                    analyticsData?.shipmentPercentage >= 0
                      ? '/images/icons/AdminHome/upArrow.svg'
                      : '/images/icons/AdminHome/downArrow.svg'
                  }
                  alt="trend"
                  style={{ width: '10px', height: '12px' }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: analyticsData?.shipmentPercentage >= 0 ? 'success.main' : 'error.main',
                    fontSize: '12px',
                  }}
                >
                  {formatNumber(analyticsData?.shipmentPercentage, locale)}%
                </Typography>
              </Box>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '28px', sm: '32px' },
                  lineHeight: 1.2,
                  mb: 0.5,
                  fontFeatureSettings: '"tnum" on, "lnum" on',
                }}
              >
                {analyticsData?.totalRevenue && formatNumber(analyticsData?.totalRevenue, locale)}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  fontSize: '14px',
                }}
              >
                Total Revenue ({defaultOrgCountryCurrency})
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default TopPerformanceView;
